import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { AhJinMark } from "../components/AhJinLogo";
import { Loader2, Mail, Building2, ArrowLeft, RefreshCcw, KeyRound, LogOut } from "lucide-react";
import AnalyticsDashboard from "../components/AnalyticsDashboard";
import CountriesPanel from "../components/CountriesPanel";
import CrmPanel from "../components/CrmPanel";
import ChatLeadsPanel from "../components/ChatLeadsPanel";
import PaymentsPanel from "../components/PaymentsPanel";
import ActivityPanel from "../components/ActivityPanel";
import VisitorsPanel from "../components/VisitorsPanel";
import SecurityPanel from "../components/SecurityPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DEVICE_STORAGE = "ahjin_admin_device";
const SIGNED_IN = "session";

const getDeviceId = () => {
  let id = localStorage.getItem(DEVICE_STORAGE);
  if (!id) {
    id = `dev-${Date.now()}-${crypto.getRandomValues(new Uint32Array(4)).join("-")}`;
    localStorage.setItem(DEVICE_STORAGE, id);
  }
  return id;
};

const Admin = () => {
  // "session" once the httpOnly admin cookie is valid; the admin key itself is never stored
  const [adminKey, setAdminKey] = useState("");
  const [keyInput, setKeyInput] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("inquiries");
  const [mfaKey, setMfaKey] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);

  useEffect(() => {
    axios.defaults.headers.common["X-Admin-Device"] = getDeviceId();
    axios.get(`${API}/admin/totp/status`).then(() => setAdminKey(SIGNED_IN)).catch(() => {});
    return () => {
      delete axios.defaults.headers.common["X-Admin-Device"];
    };
  }, []);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await axios.get(`${API}/contact`);
      setItems(data || []);
    } catch (e) {
      if (e.response?.status === 401) {
        setAdminKey("");
        setErr(e.response?.data?.detail === "session_expired" ? "Session ended. Please unlock again." : "Invalid admin key.");
      } else if (e.response?.status === 403) {
        setAdminKey("");
        setErr(e.response?.data?.detail || "This device is not authorized.");
      } else {
        setErr("Failed to load inquiries.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (adminKey) load();
  }, [adminKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitKey = async (e) => {
    e.preventDefault();
    const k = keyInput.trim();
    if (!k) return;
    setErr("");
    try {
      await axios.post(
        `${API}/admin/register-device`,
        { device_id: getDeviceId(), label: navigator.platform || "browser" },
        { headers: { "X-Admin-Key": k } }
      );
      setKeyInput("");
      await signIn(k, {});
    } catch (e2) {
      const st = e2.response?.status;
      if (st === 401) setErr("Invalid admin key.");
      else if (st === 403) setErr(e2.response?.data?.detail || "This device is not authorized.");
      else if (st === 429) setErr("Too many attempts. Try again later.");
      else setErr("Could not verify this device. Try again.");
    }
  };

  async function signIn(k, body) {
    try {
      await axios.post(`${API}/admin/login`, body, { headers: { "X-Admin-Key": k } });
      setMfaKey("");
      setMfaCode("");
      setAdminKey(SIGNED_IN);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d === "mfa_required") { setMfaKey(k); return; }
      if (d === "invalid_code") { setMfaKey(k); setErr("That code didn't work. Try the newest one."); return; }
      throw e;
    }
  }

  const submitMfa = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await signIn(mfaKey, useRecovery ? { recovery_code: mfaCode.trim() } : { code: mfaCode.trim() });
    } catch (e2) {
      setErr(e2.response?.status === 429 ? e2.response.data.detail : "Could not sign in. Try again.");
    }
  };

  const logout = () => {
    axios.post(`${API}/admin/logout`).catch(() => {});
    setAdminKey("");
    setTab("inquiries");
    setErr("");
    setItems([]);
  };

  if (!adminKey && mfaKey) {
    return (
      <main className="min-h-screen bg-[#050b1c] text-zinc-100 flex items-center justify-center px-6" data-testid="admin-mfa-page">
        <form onSubmit={submitMfa} className="w-full max-w-md rounded-2xl border border-white/10 bg-white/[0.02] p-8" data-testid="admin-mfa-form">
          <div className="flex items-center justify-between mb-8">
            <AhJinMark />
            <span className="text-[10px] uppercase tracking-[0.4em] text-purple-400">2-step</span>
          </div>
          <label className="text-[10px] uppercase tracking-[0.35em] text-zinc-400 mb-3 block">
            {useRecovery ? "Recovery code" : "Code from your authenticator app"}
          </label>
          <input
            value={mfaCode}
            onChange={(e) => setMfaCode(useRecovery ? e.target.value : e.target.value.replace(/\D/g, "").slice(0, 6))}
            inputMode={useRecovery ? "text" : "numeric"}
            autoFocus
            placeholder={useRecovery ? "e.g. 3f9a1c0b2d" : "123456"}
            data-testid="admin-mfa-input"
            className="w-full rounded-lg bg-black/40 border border-white/10 px-4 py-3 text-sm tracking-[0.3em] text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-purple-500/50"
          />
          {err && <div className="text-red-400 text-xs mt-3" data-testid="admin-mfa-error">{err}</div>}
          <button type="submit" data-testid="admin-mfa-submit" className="mt-6 w-full rounded-lg bg-purple-700 hover:bg-purple-600 transition-colors py-3 text-[11px] uppercase tracking-[0.35em]">
            Verify
          </button>
          <button type="button" onClick={() => { setUseRecovery(!useRecovery); setMfaCode(""); setErr(""); }} data-testid="admin-mfa-toggle-recovery" className="block mx-auto mt-5 text-xs text-zinc-500 hover:text-purple-300">
            {useRecovery ? "Use authenticator code instead" : "Lost your phone? Use a recovery code"}
          </button>
        </form>
      </main>
    );
  }

  if (!adminKey) {
    return (
      <main className="min-h-screen bg-[#050b1c] text-zinc-100 flex items-center justify-center px-6" data-testid="admin-login-page">
        <form
          onSubmit={submitKey}
          className="w-full max-w-md rounded-2xl border border-white/10 bg-white/[0.02] p-8"
          data-testid="admin-key-form"
        >
          <div className="flex items-center justify-between mb-8">
            <AhJinMark />
            <span className="text-[10px] uppercase tracking-[0.4em] text-purple-400">Admin</span>
          </div>
          <label className="text-[10px] uppercase tracking-[0.35em] text-zinc-400 flex items-center gap-2 mb-3">
            <KeyRound size={12} /> Admin key
          </label>
          <input
            type="password"
            value={keyInput}
            onChange={(e) => setKeyInput(e.target.value)}
            placeholder="Enter admin key"
            autoFocus
            data-testid="admin-key-input"
            className="w-full rounded-lg bg-black/40 border border-white/10 px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-purple-500/50"
          />
          {err && <div className="text-red-400 text-xs mt-3" data-testid="admin-key-error">{err}</div>}
          <button
            type="submit"
            data-testid="admin-key-submit"
            className="mt-6 w-full rounded-lg bg-purple-700 hover:bg-purple-600 transition-colors py-3 text-[11px] uppercase tracking-[0.35em]"
          >
            Unlock
          </button>
          <Link to="/" className="block text-center mt-6 text-xs text-zinc-500 hover:text-purple-300" data-testid="admin-back-link">
            ← Back to site
          </Link>
        </form>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#050b1c] text-zinc-100" data-testid="admin-page">
      <header className="sticky top-0 z-30 bg-black/70 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center gap-2 text-zinc-400 hover:text-purple-300 text-xs uppercase tracking-[0.3em]" data-testid="admin-back-link">
              <ArrowLeft size={14} /> Site
            </Link>
            <AhJinMark />
            <span className="text-[10px] uppercase tracking-[0.4em] text-purple-400 ml-2 hidden sm:inline">/ Admin · Inquiries</span>
          </div>
          <div className="flex items-center gap-5">
            <button
              onClick={() => load()}
              data-testid="admin-refresh-btn"
              className="text-xs uppercase tracking-[0.3em] text-zinc-300 hover:text-purple-300 inline-flex items-center gap-2"
            >
              <RefreshCcw size={13} /> Refresh
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout-btn"
              className="text-xs uppercase tracking-[0.3em] text-zinc-500 hover:text-red-400 inline-flex items-center gap-2"
            >
              <LogOut size={13} /> Lock
            </button>
          </div>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="flex flex-wrap items-center gap-2 mb-10">
          {[
            { id: "inquiries", label: `Inquiries (${items.length})` },
            { id: "crm", label: "CRM" },
            { id: "chatleads", label: "Chat Leads" },
            { id: "payments", label: "Payments" },
            { id: "activity", label: "Activity" },
            { id: "visitors", label: "Visitors" },
            { id: "analytics", label: "Analytics" },
            { id: "countries", label: "Countries" },
            { id: "security", label: "Security" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`px-5 py-2.5 rounded-full text-[11px] uppercase tracking-[0.3em] border transition-colors ${
                tab === t.id
                  ? "bg-purple-700 border-purple-500 text-white"
                  : "border-white/10 text-zinc-400 hover:text-purple-300 hover:border-purple-500/30"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "analytics" && <AnalyticsDashboard adminKey={adminKey} />}

        {tab === "crm" && <CrmPanel adminKey={adminKey} />}

        {tab === "chatleads" && <ChatLeadsPanel adminKey={adminKey} />}

        {tab === "payments" && <PaymentsPanel adminKey={adminKey} />}

        {tab === "activity" && <ActivityPanel adminKey={adminKey} />}
        {tab === "visitors" && <VisitorsPanel adminKey={adminKey} />}

        {tab === "countries" && <CountriesPanel adminKey={adminKey} />}
        {tab === "security" && <SecurityPanel adminKey={adminKey} />}

        {tab === "inquiries" && (
        <>
        {loading && (
          <div className="flex items-center gap-3 text-zinc-400">
            <Loader2 className="animate-spin" size={16} /> Loading…
          </div>
        )}

        {err && !loading && <div className="text-red-400 text-sm">{err}</div>}

        {!loading && !err && items.length === 0 && (
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-12 text-center text-zinc-500" data-testid="admin-empty">
            No inquiries yet. They will appear here as they come in.
          </div>
        )}

        <div className="space-y-4">
          {items.map((it) => (
            <article
              key={it.id}
              className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 hover:border-purple-500/30 transition-colors"
              data-testid={`inquiry-${it.id}`}
            >
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div>
                  <div className="font-display text-xl text-zinc-100">{it.name}</div>
                  <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-zinc-400">
                    <a href={`mailto:${it.email}`} className="inline-flex items-center gap-2 hover:text-purple-300">
                      <Mail size={13} /> {it.email}
                    </a>
                    {it.company && (
                      <span className="inline-flex items-center gap-2">
                        <Building2 size={13} /> {it.company}
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-[0.35em] text-purple-400">{it.service}</div>
                  <div className="text-xs text-zinc-500 mt-1">
                    {new Date(it.created_at).toLocaleString()}
                  </div>
                </div>
              </div>
              <p className="mt-5 text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap">
                {it.message}
              </p>
            </article>
          ))}
        </div>
        </>
        )}
      </section>
    </main>
  );
};

export default Admin;
